import * as React from "react"
import { useNavigate } from "react-router-dom"

import debounce from "lodash.debounce"

import passwordStrength from "@podcastsoundboard/ps-lib/api/auth/passwordStrength"
import signup from "@podcastsoundboard/ps-lib/api/auth/signup"
import PageContainer from "@podcastsoundboard/ps-lib/components/PageContainer"

import emailValid from "@podcastsoundboard/ps-lib/helpers/validation/emailValid"
import AppBar from "../../components/AppBar"
import Footer from "../../components/Footer"
import FullPageContentContainer from "../../components/FullPageContentContainer"
import googleAnalytics from "../../lib/googleAnalytics"
import { useAppDispatch, useAppSelector } from "../../redux"
import { setCurrentUserProfile } from "../../redux/currentUserProfile"
import { addSnackbar } from "../../redux/snackbars"
import { AxiosError } from "axios"
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  LoadingButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import LockOutlinedIcon from "@podcastsoundboard/ps-lib/ui/icons/LockOutlined"

const FormControlLabelStyled = (
  props: React.ComponentProps<typeof FormControlLabel>,
) => <FormControlLabel {...props} className="text-text-primary" />

export default function SignUp() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [submitting, setSubmitting] = React.useState(false)

  const [email, setEmail] = React.useState("")
  const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] =
    React.useState("")
  const [subscribe, setSubscribe] = React.useState(true)
  const [password, setPassword] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")

  const [errorMessage, setErrorMessage] = React.useState("")

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    setSubmitting(true)
    try {
      const profile = await signup({
        email,
        password,
        subscribedToNewsletter: subscribe,
      })

      setSubmitting(false)

      dispatch(setCurrentUserProfile(profile))
      dispatch(addSnackbar({ text: "Sign up successful." }))

      googleAnalytics.event({
        category: "user",
        action: "signup",
      })

      navigate("/app/confirm-email")
    } catch (err) {
      console.error(err)
      setSubmitting(false)
      setErrorMessage((err as Error | AxiosError).message)
    }
  }

  React.useEffect(() => {
    const debounced = debounce(() => {
      if (!password) return setPasswordValidationErrorMessage("")

      if (password && confirmPassword && password !== confirmPassword) {
        return setPasswordValidationErrorMessage("Passwords do not match.")
      }

      passwordStrength(password)
        .then(({ errorMessage }) => {
          setPasswordValidationErrorMessage(errorMessage)
        })
        .catch((err) => {
          console.error("Error checking password strength", err)
          dispatch(
            addSnackbar({
              text: "Error checking password strength.",
            }),
          )
        })
    }, 200)

    debounced()

    return debounced.cancel
  }, [confirmPassword, dispatch, password, setPasswordValidationErrorMessage])

  const emailValidationErrorMessage =
    email && !emailValid(email) && "Email invalid."
  const submitDisabled = !(
    email &&
    password &&
    confirmPassword &&
    !passwordValidationErrorMessage &&
    !emailValidationErrorMessage
  )
  const validationErrorMessage =
    emailValidationErrorMessage || passwordValidationErrorMessage
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)
  return (
    <PageContainer darkModeEnabled={darkModeEnabled} className="min-h-screen">
      <AppBar title="Podcast Soundboard" />

      <Toolbar />

      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <FullPageContentContainer maxWidth="xs">
        <Paper className="p-8 mt-8 flex flex-col items-center">
          <Avatar className="m-2 bg-primary">
            <LockOutlinedIcon />
          </Avatar>

          <Typography component="h1" variant="h5" color="text.primary">
            Sign Up
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            className="mt-6"
            width="100%"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabelStyled
                  color="text.primary"
                  control={
                    <Checkbox
                      checked={subscribe}
                      onChange={(e) => setSubscribe(e.target.checked)}
                    />
                  }
                  label="I want to know about updates."
                />
              </Grid>
            </Grid>

            {validationErrorMessage && (
              <Typography className="mt-4" color="error">
                {validationErrorMessage}
              </Typography>
            )}

            <LoadingButton
              disabled={submitDisabled}
              type="submit"
              loading={submitting}
              fullWidth
              variant="contained"
              className="mt-6 mb-4"
            >
              Sign Up
            </LoadingButton>

            <Grid container justifyContent="center">
              <Grid item>
                <Link href="/app/signin" variant="body2">
                  Sign In
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </FullPageContentContainer>

      <Footer />
    </PageContainer>
  )
}
